/**
 * Parent-side theme editor. Provides color pickers for CodeStitch CSS variables
 * with live preview via postMessage (no iframe re-render needed).
 * Uses transactions so an entire color-editing session is a single undo entry.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { bridge } from '../editing/bridge.js';
import { THEME_COLOR_DEFS, normalizeThemeTokens } from '../shared/theme-tokens.js';

let activeTransaction = null;

export function getThemeVars() {
  return THEME_COLOR_DEFS;
}

/**
 * Push a single theme token to BOTH the preview iframe (live, no re-render) AND
 * the parent document root. Parent-side glass-panel swatch CSS reads bare
 * `var(--primary)` etc.; without the parent-root mirror those vars are undefined
 * in the parent document and always resolve to their hardcoded fallback, so the
 * theme dots showed the wrong colors for every non-default palette.
 *
 * Every `update-theme-var` sender MUST route through this — never call
 * `bridge.send('update-theme-var', …)` directly.
 */
export function setThemeVar(name, value) {
  bridge.send('update-theme-var', { name, value });
  if (name && name.startsWith('--') && typeof document !== 'undefined') {
    document.documentElement.style.setProperty(name, value == null ? '' : String(value));
  }
}

/**
 * Mirror the full theme onto the parent document root. Called at boot and on
 * every `state-changed` so the parent palette never drifts from state (load,
 * undo/redo, or an edit from another surface). `includeDerived` fills in derived
 * tokens (`--accent`, `--primaryDark`, …) a panel might reference.
 */
export function mirrorThemeToParentRoot(theme) {
  if (typeof document === 'undefined') return;
  const normalized = normalizeThemeTokens(theme || {}, {
    preserveExisting: true,
    includeDerived: true
  });
  const root = document.documentElement;
  for (const [name, value] of Object.entries(normalized)) {
    if (name.startsWith('--') && value != null && value !== '') {
      root.style.setProperty(name, String(value));
    }
  }
}

/**
 * Returns true if a theme color transaction is currently open.
 */
export function hasOpenTransaction() {
  return activeTransaction !== null;
}

export function updateThemeVar(name, value) {
  // If a transaction is open for a different var, commit it first
  if (activeTransaction && activeTransaction !== name) {
    commitThemeChange();
  }

  if (!activeTransaction) {
    store.beginTransaction('Change theme color');
    activeTransaction = name;
  }

  store.update(`theme.${name}`, value, 'Change theme color');
  setThemeVar(name, value);
}

export function commitThemeChange() {
  if (activeTransaction) {
    store.commitTransaction();
    activeTransaction = null;
  }
}

export function abortThemeChange() {
  if (activeTransaction) {
    store.abortTransaction();
    activeTransaction = null;

    // Re-sync iframe with reverted state
    const theme = store.getState()?.theme;
    if (theme) {
      for (const [name, value] of Object.entries(theme)) {
        if (name.startsWith('--')) {
          setThemeVar(name, value);
        }
      }
    }
  }
}

export function getCurrentTheme() {
  const state = store.getState();
  return state ? state.theme : {};
}

export function initThemeEditor() {
  // Commit any open theme transaction when unrelated actions start.
  // This prevents leaked transactions from grouping unrelated edits.
  eventBus.on('iframe:section-clicked', () => commitThemeChange());
  eventBus.on('iframe:element-clicked', () => commitThemeChange());
  eventBus.on('editing:text-started', () => commitThemeChange());
  eventBus.on('iframe:section-action', () => commitThemeChange());
}
